/* @flow */
import * as React from 'react';
import Collapsible from 'react-collapsible';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import { get } from 'lodash';

import styles from './styles.module.scss';
import ActionButton from '../ActionButton';
import Checkbox from '../Checkbox';
import ErrorAwareImage from '../ErrorAwareImage';
import Icon from '../Icon';
import PeopleAggregation from '../PeopleAggregation';
import Table from '../Table';
import TrackListRowAccordion from '../TrackListRowAccordion';
import protonesUtils from '../../utils/protones';
import stringUtils from '../../utils/string';
import playArrowImg from '../../assets/images/play-arrow-white.svg';
import pauseImg from '../../assets/images/pause-white.svg';
import equalizerPlayingImg from '../../assets/images/equalizer-playing-white.gif';
import equalizerStoppedImg from '../../assets/images/equalizer-stopped-white.png';

import type {
  Translate,
  TrackMedia,
  SimplifiedTrack,
  SimplifiedMedia,
  Person,
  Usage,
  SaveUsageParams,
  MergedLicenseComponent,
} from '../../types';

import type { TabId } from '../TrackListRowAccordion';

type Props = {
  track: TrackMedia | SimplifiedTrack,
  medias?: SimplifiedMedia[],
  isWorklistItem?: boolean,
  usage?: Usage,
  isReadOnlyUsage?: boolean,
  onCreateProduction?: () => mixed,
  onSaveUsage?: (params: SaveUsageParams) => any,
  isSelected?: boolean,
  isSelectedIndeterminate?: boolean,
  isSelectable?: boolean,
  isCurrentTrack?: boolean,
  isPlaying?: boolean,
  components?: MergedLicenseComponent[],
  selectedComponents?: string[],
  onComponentCheckboxClick?: (componentId: string) => mixed,
  onComponentHeaderCheckboxClick?: () => mixed,
  isUnplayable?: boolean,
  onCheckboxClick?: (index: number) => mixed,
  onPlayClick?: () => mixed,
  onMediaClick?: (media: SimplifiedMedia) => mixed,
  onPerformerClick?: (performer: Person) => mixed,
  onAuthorClick?: (author: Person) => mixed,
  onAskQuotationForComponents?: (components: MergedLicenseComponent[]) => mixed,
  onBuyComponents?: (components: MergedLicenseComponent[]) => mixed,
  isLoading?: boolean,
  isDetailsExpanded?: boolean,
  onDetailsExpand?: (isExpanded: boolean) => any,
  showTrackNumber?: boolean,
  showPurchaseDate?: boolean,
  purchaseDate?: number,
  showSentToCartDate?: boolean,
  sentToCartDate?: number,
  actionButtons: React.Node,
  defaultTabId?: TabId,
  t: Translate,
};

type State = {
  isRowExpanded: boolean,
  isDetailsExpanded: boolean,
  selectedTabId: TabId | null,
  isRowHovered: boolean,
};

class TrackListRow extends React.PureComponent<Props, State> {
  state = {
    isRowExpanded: false,
    isDetailsExpanded: !!this.props.isDetailsExpanded,
    selectedTabId: this.props.defaultTabId || null,
    isRowHovered: false,
  };

  componentDidUpdate(prevProps: Props) {
    const { isDetailsExpanded, isLoading } = this.props;
    if (!prevProps.isLoading && isLoading) {
      this.setState({ isRowExpanded: false, isDetailsExpanded: false });
    } else if (isDetailsExpanded != null && prevProps.isDetailsExpanded !== isDetailsExpanded) {
      this.setState({ isDetailsExpanded: isDetailsExpanded });
    }
  }

  _handleRowExpandClick = () => {
    this.setState({ isRowExpanded: !this.state.isRowExpanded });
  };

  _handleDetailsExpandClick = () => {
    const shouldExpand = !this.state.isDetailsExpanded || this.state.selectedTabId !== 'DETAILS';
    this.setState({
      selectedTabId: 'DETAILS',
      isDetailsExpanded: shouldExpand,
    });
    this.props.onDetailsExpand && this.props.onDetailsExpand(shouldExpand);
  };

  _handleUsageClick = () => {
    const shouldExpand = !this.state.isDetailsExpanded || this.state.selectedTabId !== 'USAGE';
    this.setState({
      selectedTabId: 'USAGE',
      isDetailsExpanded: shouldExpand,
    });
    this.props.onDetailsExpand && this.props.onDetailsExpand(shouldExpand);
  };

  _handlePriceClick = () => {
    const shouldExpand =
      !this.state.isDetailsExpanded || this.state.selectedTabId !== 'LICENSE_COMPONENTS';
    this.setState({
      selectedTabId: 'LICENSE_COMPONENTS',
      isDetailsExpanded: shouldExpand,
    });
    this.props.onDetailsExpand && this.props.onDetailsExpand(shouldExpand);
  };

  render() {
    const {
      isCurrentTrack,
      isSelected,
      isSelectedIndeterminate = false,
      isSelectable,
      isPlaying,
      isUnplayable,
      onPlayClick,
      onPerformerClick,
      onAuthorClick,
      onCheckboxClick,
      components = [],
      selectedComponents: selectedComponentIds = [],
      onComponentCheckboxClick,
      onComponentHeaderCheckboxClick,
      onAskQuotationForComponents,
      onBuyComponents,
      actionButtons,
      isLoading,
      showTrackNumber,
      showPurchaseDate = false,
      purchaseDate,
      showSentToCartDate = false,
      sentToCartDate,
      isWorklistItem,
      onCreateProduction,
      onSaveUsage,
      usage,
      isReadOnlyUsage = false,
      t,
    } = this.props;
    const { selectedTabId } = this.state;
    const { authors, performers, duration, title, price } = this.props.track;

    const hasComponents = components.length > 0;
    // const selectedComponents = components.filter(component =>
    //   selectedComponentIds.includes(component.id)
    // );
    // const hasSelectedComponents = selectedComponents.length > 0;
    const hasUnpricedComponents = components.some(
      component => !protonesUtils.getCanBuyLicenseComponent(component)
    );
    // const hasSelectedPricedComponents = selectedComponents.some(
    //   protonesUtils.getCanBuyLicenseComponent
    // );

    const componentsTotalDiscountedPrice = protonesUtils.getLicenseComponentsTotalDiscountedPrice(
      components
    );
    //BDP
    const componentsTotalPrice = protonesUtils.getLicenseComponentsTotalPrice(components);
    // $FlowFixMe
    const trackNumber = this.props.track.trackNumber;
    const handlePlayClick = () => {
      if (onPlayClick) onPlayClick();
    };
    let imageSrc;
    let imageAlt;
    if (isCurrentTrack && isPlaying) {
      imageSrc = equalizerPlayingImg;
      imageAlt = t('PLAYING');
    } else if (isCurrentTrack) {
      imageSrc = equalizerStoppedImg;
      imageAlt = t('PAUSE');
    } else {
      imageSrc = undefined;
    }
    // $FlowFixMe
    const oldestMedia = protonesUtils.getOldestMedia(this.props.medias);

    const computedUsage = Object.assign({}, usage);
    const hasUsage = !!usage;
    const area = get(computedUsage, 'production.area.name', '');
    const production = get(computedUsage, 'production.name', '');
    const usageType = get(computedUsage, 'usageType.name', '');
    const episode = get(computedUsage, 'episode', '');
    return (
      <Table.Row
        isLoading={isLoading}
        onMouseEnter={() => this.setState({ isRowHovered: true })}
        onMouseLeave={() => this.setState({ isRowHovered: false })}
        after={
          this.state.isDetailsExpanded ? (
            <Collapsible
              open={this.state.isDetailsExpanded}
              transitionTime={200}
              easing={'ease-in-out'}
            >
              <TrackListRowAccordion
                track={this.props.track}
                medias={this.props.medias}
                usage={computedUsage}
                isReadOnlyUsage={isReadOnlyUsage}
                onSaveUsage={onSaveUsage}
                hasUsage={hasUsage}
                components={this.props.components}
                selectedComponents={selectedComponentIds}
                onComponentCheckboxClick={onComponentCheckboxClick}
                onComponentHeaderCheckboxClick={onComponentHeaderCheckboxClick}
                onAskQuotationForComponents={onAskQuotationForComponents}
                selectedTabId={selectedTabId}
                isWorklistItem={isWorklistItem}
                onCreateProduction={onCreateProduction}
                onMediaClick={this.props.onMediaClick}
                onBuyComponents={onBuyComponents}
                isLoading={isLoading}
              />
            </Collapsible>
          ) : (
            undefined
          )
        }
      >
        {isSelectable && (
          <Table.DataCell>
            <Checkbox
              onClick={onCheckboxClick}
              small={true}
              checked={isSelected || isSelectedIndeterminate}
              isIndeterminate={isSelectedIndeterminate}
            />
          </Table.DataCell>
        )}
        <Table.DataCell
          onClick={
            // $FlowFixMe
            oldestMedia !== undefined ? () => this.props.onMediaClick(oldestMedia) : undefined
          }
        >
          <ErrorAwareImage
            // $FlowFixMe
            src={protonesUtils.getTrackCoverUrl(this.props.track)}
            className={styles.albumImage}
            alt={t('ALBUM_COVER')}
          />
        </Table.DataCell>
        <Table.DataCell
          style={{ transform: 'translateX(-1em)' }}
          onClick={isUnplayable ? () => null : handlePlayClick}
        >
          {imageSrc ? (
            <img src={imageSrc} className={styles.albumImage} alt={imageAlt} />
          ) : (
            <div className={styles.albumImage} />
          )}
          {!isUnplayable && (
            <img
              src={isCurrentTrack && isPlaying ? pauseImg : playArrowImg}
              className={styles.playArrowOverlay}
              style={
                this.state.isRowHovered
                  ? { display: 'initial', backgroundColor: 'rgba(0, 0, 0, 0.7)' }
                  : { display: 'none' }
              }
              alt={isCurrentTrack && isPlaying ? t('PAUSE') : t('PLAY')}
            />
          )}
        </Table.DataCell>
        {showTrackNumber && <Table.DataCell>{trackNumber || '-'}</Table.DataCell>}
        <Table.DataCell>{title}</Table.DataCell>
        <Table.DataCell>
          <PeopleAggregation
            people={performers}
            onPersonClick={onPerformerClick}
            onToggleExpand={this._handleRowExpandClick}
            isExpanded={this.state.isRowExpanded}
            emptyText={t('NO_PERFORMER')}
          />
        </Table.DataCell>
        <Table.DataCell>
          <PeopleAggregation
            people={authors}
            onPersonClick={onAuthorClick}
            onToggleExpand={this._handleRowExpandClick}
            isExpanded={this.state.isRowExpanded}
            emptyText={t('NO_AUTHOR')}
          />
        </Table.DataCell>
        {hasUsage && <Table.DataCell>{area}</Table.DataCell>}
        {hasUsage && <Table.DataCell>{production}</Table.DataCell>}
        {hasUsage && <Table.DataCell>{episode}</Table.DataCell>}
        {hasUsage && <Table.DataCell>{usageType}</Table.DataCell>}
        {hasUsage && (
          <Table.DataCell onClick={this._handleUsageClick}>
            <ActionButton iconName={'usage'} tooltip={t('USAGE_DETAILS')} />
            <Icon
              name={this.state.isDetailsExpanded ? 'chevron_up' : 'chevron_down'}
              style={{ margin: '-5px' }}
            />
          </Table.DataCell>
        )}
        <Table.DataCell>{stringUtils.prettifyTime(duration)}</Table.DataCell>
        <Table.DataCell onClick={hasComponents ? this._handlePriceClick : undefined}>
          <div className={styles.priceValue}>
            {hasComponents
              ? stringUtils.prettifyMoney(
                  componentsTotalDiscountedPrice !== 0
                    ? componentsTotalDiscountedPrice
                    : componentsTotalPrice
                ) //BDP il prezzo  � determinato da calcolisu tabelle e non viene pi� considerato quello contenuto nella track json
              : stringUtils.prettifyMoney(price || 0)}
            {hasComponents && (
              <Icon name={this.state.isDetailsExpanded ? 'chevron_up' : 'chevron_down'} />
            )}
          </div>
          {hasUnpricedComponents && (
            <div className={styles.priceWarning}>
              <ActionButton iconName={'ask'} tooltip={t('ASK_QUOTATION')} />
            </div>
          )}
        </Table.DataCell>
        {!!showPurchaseDate && (
          <Table.DataCell>{moment(purchaseDate).format('YYYY / MM / DD')}</Table.DataCell>
        )}
        {!!showSentToCartDate && (
          <Table.DataCell>{moment(sentToCartDate).format('YYYY / MM / DD')}</Table.DataCell>
        )}
        <Table.DataCell>
          <div className={styles.actionButtonsContainer}>
            {actionButtons}
            <ActionButton
              key={String(this.state.isDetailsExpanded)}
              iconName={this.state.isDetailsExpanded ? 'expand_less' : 'expand_more'}
              tooltip={this.state.isDetailsExpanded ? t('COLLAPSE') : t('EXPAND')}
              onClick={this._handleDetailsExpandClick}
            />
          </div>
        </Table.DataCell>
      </Table.Row>
    );
  }
}

export default withTranslation()(TrackListRow);
